import { Fragment, useState } from "react";
import useSWR from "swr";

import { ApprovalTimeline } from "../components/ApprovalTimeline";
import { Icon } from "../components/Icon";
import { StatusPill } from "../components/StatusPill";
import { useModalDismiss } from "../hooks/useModalDismiss";
import { fmtDateTime, nextActionLine, requestTimeline } from "../utils/requestTimeline";
import { api, type RequestDetail, type RequestListRow } from "../services/api";

const PAGE_SIZE = 50;

interface ReqFilter {
  id: string;
  label: string;
  match: (r: RequestListRow) => boolean;
}

const REQ_FILTERS: ReqFilter[] = [
  { id: "all", label: "All", match: () => true },
  { id: "open", label: "Open", match: (r) => r.state === "pending" || r.state === "submitted" },
  {
    id: "approved",
    label: "Approved",
    match: (r) => r.state === "approved" || r.state === "auto_approved",
  },
  {
    id: "attention",
    label: "Needs attention",
    match: (r) => r.state === "rejected" || r.state === "expired",
  },
];

function humanizeSlug(slug: string | null): string {
  if (!slug) return "Request";
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Short, low-key outcome hint per row (the holder's name lives in the detail). */
function outcomeHint(r: RequestListRow): string {
  switch (r.state) {
    case "pending":
      return `Step ${(r.current_step_index ?? 0) + 1} · awaiting approval`;
    case "submitted":
      return "With the service desk";
    case "approved":
      return "Approved";
    case "auto_approved":
      return "Auto-approved";
    case "rejected":
      return r.rejection_reason ? `Rejected · ${r.rejection_reason}` : "Rejected";
    case "expired":
      return "Expired";
    case "cancelled":
      return "Cancelled";
    default:
      return "";
  }
}

/**
 * My Requests — every approval request the caller has submitted, in the
 * "Online Forms" look: pill-tab filters, status pills, and a detail slide-over
 * carrying the live approval timeline + Crystal-Comms next action.
 */
export function MyRequests() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  const { data, isLoading, error, mutate } = useSWR<RequestListRow[]>(
    ["/api/requests", page],
    () => api.requests.list(undefined, page, PAGE_SIZE),
  );
  const hasNextPage = (data?.length ?? 0) === PAGE_SIZE;

  const rows = (data ?? []).filter(REQ_FILTERS.find((f) => f.id === filter)!.match);

  return (
    <>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", gap: 24 }}>
        <div>
          <h1 className="page-title">My Requests</h1>
          <p className="page-subtitle">
            Every form you&apos;ve submitted, with live approval status. You&apos;ll always see
            who&apos;s holding it and what happens next.
          </p>
        </div>
      </div>

      <div className="pill-tabs">
        {REQ_FILTERS.map((f) => {
          const count = (data ?? []).filter(f.match).length;
          return (
            <button
              key={f.id}
              type="button"
              className={"pill-tab" + (filter === f.id ? " on" : "")}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              <span className="pill-tab-count">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th style={{ width: "40%" }}>Request</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Progress / outcome</th>
                <th aria-label="open" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td className="ticket-empty" colSpan={5}>
                    Loading&hellip;
                  </td>
                </tr>
              )}
              {error && !isLoading && (
                <tr>
                  <td className="ticket-empty" colSpan={5}>
                    {String((error as Error).message ?? error)}
                  </td>
                </tr>
              )}
              {!isLoading && !error && rows.length === 0 && (
                <tr>
                  <td className="ticket-empty" colSpan={5}>
                    No requests in this view.
                  </td>
                </tr>
              )}
              {!isLoading &&
                !error &&
                rows.map((r) => (
                  <tr key={r.id} className="ticket-table-row" onClick={() => setSelectedId(r.id)}>
                    <td>
                      <div className="t-title">{humanizeSlug(r.form_type_slug)}</div>
                      <div className="t-id">{r.id.slice(0, 8)}</div>
                    </td>
                    <td style={{ whiteSpace: "nowrap", color: "var(--iq-text-muted)" }}>
                      {fmtDateTime(r.submitted_at)}
                    </td>
                    <td>
                      <StatusPill state={r.state} />
                    </td>
                    <td style={{ color: "var(--iq-text-muted)", fontSize: 13.5 }}>
                      {outcomeHint(r)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Icon name="chevRight" size={18} style={{ color: "var(--iq-text-disabled)" }} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {(page > 1 || hasNextPage) && (
          <div style={{ display: "flex", gap: 8, padding: 16, alignItems: "center" }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span style={{ fontSize: 13, color: "var(--iq-text-muted)" }}>Page {page}</span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNextPage}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {selectedId && (
        <RequestDetailSlideover
          requestId={selectedId}
          onClose={() => setSelectedId(null)}
          onCancelled={async () => {
            setSelectedId(null);
            await mutate();
          }}
        />
      )}
    </>
  );
}

function humanizeKey(key: string): string {
  return key.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function displaySnapshotValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map((v) => String(v)).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function RequestDetailSlideover({
  requestId,
  onClose,
  onCancelled,
}: {
  requestId: string;
  onClose: () => void;
  onCancelled: () => void | Promise<void>;
}) {
  const { data, isLoading, error, mutate } = useSWR<RequestDetail>(
    `/api/requests/${requestId}`,
    () => api.requests.get(requestId),
  );
  const [busy, setBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const panelRef = useModalDismiss<HTMLDivElement>(onClose);

  const canCancel = data !== undefined && (data.state === "submitted" || data.state === "pending");
  const timeline = data ? requestTimeline(data) : [];
  const snapshot = data ? Object.entries(data.form_data_snapshot ?? {}) : [];

  async function cancel() {
    setBusy(true);
    setCancelError(null);
    try {
      await api.requests.cancel(requestId);
      await mutate();
      await onCancelled();
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : "Failed to cancel");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="slideover" ref={panelRef}>
        <div className="slideover-head">
          <span className="svc-icon violet" style={{ width: 44, height: 44, flex: "0 0 44px" }}>
            <Icon name="doc" size={22} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mono-label">{requestId.slice(0, 8)}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--iq-text)", marginTop: 2 }}>
              {humanizeSlug(data?.form_type_slug ?? null)}
            </div>
            {data && (
              <div style={{ marginTop: 8 }}>
                <StatusPill state={data.state} />
              </div>
            )}
          </div>
          <button type="button" className="so-close" onClick={onClose} aria-label="Close">
            <Icon name="x" size={17} />
          </button>
        </div>

        <div className="slideover-body">
          {isLoading && <p>Loading request&hellip;</p>}
          {error && !isLoading && (
            <div className="form-error">{String((error as Error).message ?? error)}</div>
          )}
          {data && (
            <>
              <div
                className={"iq-callout" + (data.state === "rejected" ? " amber" : "")}
                style={{ marginBottom: 24 }}
              >
                <Icon
                  name={data.state === "rejected" ? "alert" : "info"}
                  size={18}
                  style={{ color: data.state === "rejected" ? "#B8770F" : "var(--iq-violet-deep)" }}
                />
                <span>{nextActionLine(data)}</span>
              </div>

              {timeline.length > 0 && (
                <div className="detail-section">
                  <div className="mono-label">Approval history</div>
                  <div style={{ marginTop: 12 }}>
                    <ApprovalTimeline path={timeline} />
                  </div>
                </div>
              )}

              <div className="detail-section">
                <div className="mono-label">Submitted data</div>
                <dl className="kv" style={{ marginTop: 12 }}>
                  {snapshot.length === 0 && (
                    <span style={{ color: "var(--iq-text-muted)", fontSize: 14 }}>
                      No field data captured.
                    </span>
                  )}
                  {snapshot.map(([k, v]) => (
                    <Fragment key={k}>
                      <dt>{humanizeKey(k)}</dt>
                      <dd>{displaySnapshotValue(v)}</dd>
                    </Fragment>
                  ))}
                </dl>
              </div>

              {data.linked_autotask_ticket_number && (
                <div className="detail-section">
                  <div className="mono-label">Linked Autotask ticket</div>
                  <div
                    className="mono"
                    style={{
                      marginTop: 10,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: "var(--iq-violet-deep)",
                      fontSize: 13.5,
                    }}
                  >
                    <Icon name="link" size={14} />
                    {data.linked_autotask_ticket_number}
                  </div>
                </div>
              )}

              {cancelError && <div className="form-error">{cancelError}</div>}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Close
          </button>
          {canCancel && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void cancel()}
              disabled={busy}
            >
              {busy ? "Cancelling…" : "Cancel request"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
